import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Mail, Hash, BellRing, CheckCircle2, XCircle,
         AlertCircle, Slack, Bell, Clock, ShieldAlert, RotateCw,
         HelpCircle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * Notification Center — /settings/notifications
 *
 * Two stacked sections:
 *  1. Preferences matrix: events × channels with per-cell switches.
 *     Slack switches for workspace-scoped events are read-only on this
 *     page — they're managed in the existing Settings → Slack integration
 *     (one channel per workspace, so per-user toggling is structurally
 *     impossible). A pill explains this in the cell.
 *  2. Activity log: latest 100 dispatches with status pills.
 *
 * The catalog is fetched from the backend so labels never drift between
 * client and server.
 */
export default function Notifications() {
  return (
    <div className="space-y-8" data-testid="notification-center-page">
      <header>
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">
          Notifications
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Choose how AgentFlow keeps you in the loop. Changes apply to your account.
        </p>
      </header>

      <PreferencesCard />
      <ActivityCard />
    </div>
  );
}

// ---------------------------- Preferences ------------------------------------

const CHANNEL_META = {
  email:  { label: "Email",  Icon: Mail },
  slack:  { label: "Slack",  Icon: Slack },
  in_app: { label: "In-app", Icon: BellRing },
};

function PreferencesCard() {
  const [catalog, setCatalog] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);  // "event:channel" being saved

  useEffect(() => {
    (async () => {
      try {
        const [cat, pr] = await Promise.all([
          api.get("/notifications/catalog"),
          api.get("/notifications/preferences"),
        ]);
        setCatalog(cat.data.events);
        setPrefs(pr.data.prefs);
      } catch (err) {
        toast.error("Couldn't load notification preferences",
          { description: formatApiError(err) });
      } finally { setLoading(false); }
    })();
  }, []);

  const toggle = async (event, channel, next) => {
    const key = `${event}:${channel}`;
    setSaving(key);
    // Optimistic update — instantly reflect the toggle, roll back on error.
    const previous = prefs;
    setPrefs((p) => ({ ...p, [event]: { ...p[event], [channel]: next } }));
    try {
      const { data } = await api.patch("/notifications/preferences",
        { event, channel, enabled: next });
      setPrefs(data.prefs);
    } catch (err) {
      setPrefs(previous);
      toast.error("Couldn't save preference",
        { description: formatApiError(err) });
    } finally { setSaving(null); }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading preferences…
      </div>
    );
  }
  if (!catalog || !prefs) return null;

  // Union of all channels actually used by any event, in the order they appear
  // in the catalog. Keeps the table tidy when a channel is irrelevant for one row.
  const allChannels = ["email", "slack", "in_app"];

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
      data-testid="preferences-card"
    >
      <div className="px-6 py-4 border-b border-slate-100">
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
          Preferences
        </div>
        <p className="text-sm text-slate-600 mt-1">
          Per-event, per-channel. Some events have channels marked
          <span className="ml-1 inline-flex items-center rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">workspace</span>
          {" — "}those are managed in Settings → Slack.
        </p>
      </div>
      <div className="hidden sm:grid grid-cols-[1fr_repeat(3,_auto)] gap-x-8 gap-y-0 px-6 py-3 border-b border-slate-100 text-[11px] uppercase tracking-wider font-semibold text-slate-500">
        <div>Event</div>
        {allChannels.map((ch) => {
          const M = CHANNEL_META[ch];
          return (
            <div key={ch} className="flex items-center gap-1.5 justify-end min-w-[80px]">
              <M.Icon className="h-3 w-3" /> {M.label}
            </div>
          );
        })}
      </div>

      <ul className="divide-y divide-slate-100">
        {Object.entries(catalog).map(([event, meta]) => (
          <li key={event} className="px-6 py-4 sm:grid sm:grid-cols-[1fr_repeat(3,_auto)] sm:items-center gap-x-8" data-testid={`pref-row-${event}`}>
            <div>
              <div className="font-medium text-slate-900 text-sm">{meta.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{meta.description}</div>
            </div>
            {allChannels.map((ch) => {
              if (!(meta.channels || []).includes(ch)) {
                return <div key={ch} className="hidden sm:flex justify-end" />;
              }
              const alwaysOn = !!(meta.always_on || {})[ch];
              const isWorkspace = (meta.scope || {})[ch] === "workspace";
              const current = !!(prefs[event] || {})[ch];
              const key = `${event}:${ch}`;
              const busy = saving === key;
              return (
                <div key={ch} className="flex justify-between sm:justify-end items-center gap-2 mt-3 sm:mt-0">
                  <span className="text-xs text-slate-500 sm:hidden inline-flex items-center gap-1">
                    {React.createElement(CHANNEL_META[ch].Icon, { className: "h-3 w-3" })}
                    {CHANNEL_META[ch].label}
                  </span>
                  <div className="flex items-center gap-2">
                    {alwaysOn ? (
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-100 rounded-full px-1.5 py-0.5">
                        Always on
                      </span>
                    ) : isWorkspace ? (
                      <span
                        className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-100 rounded-full px-1.5 py-0.5"
                        title="This setting is workspace-wide. Manage it in Settings → Slack."
                      >
                        Workspace
                      </span>
                    ) : null}
                    <Switch
                      checked={current}
                      disabled={alwaysOn || isWorkspace || busy}
                      onCheckedChange={(v) => toggle(event, ch, v)}
                      data-testid={`pref-switch-${event}-${ch}`}
                    />
                  </div>
                </div>
              );
            })}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ----------------------------- Activity --------------------------------------

const STATUS_META = {
  sent: { Icon: CheckCircle2, label: "Sent", cls: "text-emerald-700 bg-emerald-50" },
  failed: { Icon: XCircle, label: "Failed", cls: "text-rose-700 bg-rose-50" },
  skipped: { Icon: AlertCircle, label: "Skipped", cls: "text-amber-700 bg-amber-50" },
  disabled_by_prefs: { Icon: AlertCircle, label: "Off by preference", cls: "text-slate-600 bg-slate-100" },
};

// Friendly copy for `meta.error.code` values. The backend's _classify_error()
// maps raw errors to these stable codes; we look them up here. Iterate copy
// without a backend redeploy.
const ERROR_LABELS = {
  channel_not_found: {
    headline: "Slack channel not found",
    help: "Re-add the AgentFlow bot to the chosen channel, or pick a different channel in Settings → Slack.",
  },
  not_in_channel: {
    headline: "Bot not in channel",
    help: "Invite AgentFlow to the channel — type `/invite @AgentFlow` in Slack.",
  },
  rate_limited: {
    headline: "Rate-limited",
    help: "The destination is throttling us. Retrying in a minute usually clears it.",
  },
  invalid_auth: {
    headline: "Slack auth expired",
    help: "Reconnect Slack in Settings → Connected accounts.",
  },
  resend_disabled: {
    headline: "Email service offline",
    help: "RESEND_API_KEY is missing or invalid in this environment.",
  },
  resend_bounce: {
    headline: "Email bounced",
    help: "The recipient inbox rejected the message. Check the email address.",
  },
  resend_invalid: {
    headline: "Invalid email address",
    help: "Double-check the recipient's email address.",
  },
  timeout: {
    headline: "Request timed out",
    help: "The destination didn't respond in time. Retrying often works.",
  },
  network: {
    headline: "Network error",
    help: "Couldn't reach the destination service. Retrying often works.",
  },
  unknown: {
    headline: "Send failed",
    help: "See the technical detail below.",
  },
};

function ActivityCard() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(null);   // activity id currently retrying

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications/activity?limit=100");
      setRows(data.activity || []);
    } catch (err) {
      toast.error("Couldn't load activity",
        { description: formatApiError(err) });
      setRows([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const retry = async (rowId) => {
    setRetrying(rowId);
    try {
      await api.post(`/notifications/activity/${rowId}/retry`);
      toast.success("Retry sent");
      await load();
    } catch (err) {
      toast.error("Retry failed", { description: formatApiError(err) });
    } finally { setRetrying(null); }
  };

  const empty = !loading && (!rows || rows.length === 0);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="activity-card">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
            Recent activity
          </div>
          <p className="text-sm text-slate-600 mt-1">
            The last 100 notifications dispatched for this workspace.
          </p>
        </div>
        <button
          onClick={load}
          className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="activity-refresh"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clock className="h-3.5 w-3.5" />}
          Refresh
        </button>
      </div>

      {empty ? (
        <div className="px-6 py-12 text-center text-sm text-slate-500" data-testid="activity-empty">
          <Bell className="h-5 w-5 mx-auto text-slate-300" />
          <div className="mt-2">No notifications dispatched yet. Run a scan to see this fill up.</div>
        </div>
      ) : loading ? (
        <div className="px-6 py-10 text-sm text-slate-500 inline-flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((r) => (
            <ActivityRow
              key={r.id}
              row={r}
              onRetry={retry}
              retrying={retrying === r.id}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// (event, channel) pairs that can be retried — must match RETRIABLE on the
// backend. Kept inline so the UI doesn't have to fetch yet another endpoint.
const RETRIABLE_PAIRS = new Set([
  "scan_critical_high:email",
  "scan_critical_high:slack",
]);

function isRetriable(row) {
  return row.status === "failed"
    && RETRIABLE_PAIRS.has(`${row.event}:${row.channel}`);
}

function retryDisabledReason(row) {
  if (row.status !== "failed") return null;
  // Specific channel reasons (matches backend retriable_reason).
  if (row.channel === "in_app") {
    return "In-app rows are the notification itself.";
  }
  if (row.event === "weekly_digest" || row.event === "daily_slack_digest") {
    return "Digests reflect current data — retrying days later would send the wrong snapshot.";
  }
  if (row.event === "workspace_invite") {
    return "Resend invitations from Settings → Members.";
  }
  if (row.event === "upgrade_reminder") {
    return "Upgrade reminders are re-triggered automatically by usage.";
  }
  return "Not retriable.";
}

function ActivityRow({ row, onRetry, retrying }) {
  const status = STATUS_META[row.status] || STATUS_META.skipped;
  const ChIcon = (CHANNEL_META[row.channel] || {}).Icon || Hash;
  const when = useMemo(() => {
    if (!row.created_at) return "";
    const d = new Date(row.created_at);
    const diffMs = Date.now() - d.getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return d.toLocaleDateString();
  }, [row.created_at]);

  return (
    <li className="px-6 py-3 flex items-center gap-4" data-testid={`activity-row-${row.id}`}>
      <ChIcon className="h-4 w-4 text-slate-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-900 truncate">
          <span className="font-medium">{eventLabel(row.event)}</span>
          {row.target ? <span className="text-slate-500"> → {row.target}</span> : null}
        </div>
        {row.meta?.scan_id ? (
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
            <ShieldAlert className="h-3 w-3" />
            {row.meta.level ? `${row.meta.level} risk · ` : ""}scan {row.meta.scan_id}
          </div>
        ) : null}
      </div>
      <StatusPill row={row} status={status} />
      <RetryControl row={row} onRetry={onRetry} retrying={retrying} />
      <span className="text-xs text-slate-400 shrink-0 w-16 text-right">{when}</span>
    </li>
  );
}

/**
 * Status pill. When the row failed AND the backend captured a `meta.error`
 * blob, the pill becomes a clickable trigger that opens a popover with a
 * human-readable reason + sanitised technical detail. Otherwise it renders
 * as a static pill (same visual).
 */
function StatusPill({ row, status }) {
  const errorMeta = row?.meta?.error;
  const isClickable = row.status === "failed" && errorMeta && errorMeta.code;

  const pill = (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${status.cls}${
        isClickable ? " cursor-pointer hover:ring-2 hover:ring-rose-200" : ""
      }`}
      data-testid={`activity-status-${row.id}`}
    >
      <status.Icon className="h-3 w-3" /> {status.label}
      {isClickable ? <HelpCircle className="h-3 w-3 opacity-70" /> : null}
    </span>
  );

  if (!isClickable) return pill;

  const friendly = ERROR_LABELS[errorMeta.code] || ERROR_LABELS.unknown;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Why did this fail?"
          data-testid={`why-failed-${row.id}`}
          className="shrink-0"
        >
          {pill}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 text-sm" data-testid={`why-failed-popover-${row.id}`}>
        <div className="flex items-start gap-2">
          <XCircle className="h-4 w-4 text-rose-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 leading-tight">
              {friendly.headline}
            </div>
            <div className="text-xs text-slate-600 mt-1 leading-relaxed">
              {friendly.help}
            </div>
            {errorMeta.message ? (
              <details className="mt-3 text-xs text-slate-500">
                <summary className="cursor-pointer select-none hover:text-slate-900" data-testid={`why-failed-detail-toggle-${row.id}`}>
                  Technical detail
                </summary>
                <pre className="mt-2 whitespace-pre-wrap break-words font-mono text-[11px] text-slate-600 bg-slate-50 rounded p-2 border border-slate-200">
                  {errorMeta.message}
                </pre>
              </details>
            ) : null}
            {errorMeta.captured_at ? (
              <div className="mt-2 text-[10px] uppercase tracking-wider font-semibold text-slate-400">
                Captured {new Date(errorMeta.captured_at).toLocaleString()}
              </div>
            ) : null}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Inline retry control on failed rows.
 *  - Retriable + failed  → small "Retry" link
 *  - Failed but non-retriable → muted "—" with a tooltip explaining why
 *  - Anything else (sent, skipped) → no element rendered (collapses)
 */
function RetryControl({ row, onRetry, retrying }) {
  if (row.status !== "failed") return null;   // collapses to nothing
  const reason = retryDisabledReason(row);
  if (!isRetriable(row)) {
    // We render a muted hint instead of nothing so users can see WHY
    // some failed rows have no retry option.
    return (
      <span
        className="text-[11px] text-slate-400 italic shrink-0"
        title={reason || "Not retriable"}
        data-testid={`retry-disabled-${row.id}`}
      >
        Not retriable
      </span>
    );
  }
  return (
    <button
      onClick={() => onRetry(row.id)}
      disabled={retrying}
      className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-50"
      data-testid={`retry-${row.id}`}
    >
      {retrying ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
      Retry
    </button>
  );
}

const EVENT_LABEL_FALLBACKS = {
  scan_critical_high: "Critical/High scan",
  scan_any: "Scan completed",
  weekly_digest: "Weekly digest",
  daily_slack_digest: "Daily Slack digest",
  upgrade_reminder: "Upgrade reminder",
  workspace_invite: "Workspace invite",
};
function eventLabel(ev) { return EVENT_LABEL_FALLBACKS[ev] || ev; }
